/* eslint-disable react/no-unescaped-entities */
import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Joyride, ACTIONS, EVENTS } from 'react-joyride'
import { withUserStore } from '../../contexts/UserStore'
import { 
  createPrimerStep, 
  navigationSteps, 
  contextSteps,
  sequences
} from './TourSteps'

const TeamLeadTour = props => {
  const [stepIndex, setStepIndex] = useState(0)
  const [isRunning, setIsRunning] = useState(false)
  const [hasShownAutoTour, setHasShownAutoTour] = useState(false)
  
  // Detect account type
  const getAccountType = () => {
    if (!props.userStore?.account) return 'business'
    return props.userStore.account.accountType || 'business'
  }

  // Auto-show tour for new team leads
  useEffect(() => {
    if (!props.userStore || !props.userStore.currentAccountsUser || hasShownAutoTour) return
    
    // Skip auto-showing tour if we're in a test environment
    if (window.Cypress || window.location.search.includes('test')) return
    
    const accountsUser = props.userStore.currentAccountsUser
    const strategies = props.userStore.getRecords('strategies') || []
    const goals = props.userStore.getRecords('goals') || []
    
    // Check if user is a team lead
    const isTeamLead = accountsUser.teamRole === 'team_lead'
    
    // Check if user has seen the tour before (using settings)
    const hasSeenTour = props.userStore.getAccountsUserSetting('hasSeenTeamLeadTour')
    
    // Show tour if:
    // 1. User is a team lead
    // 2. Has not seen the tour before
    // 3. Has no strategies or goals yet (new to the account)
    if (isTeamLead && !hasSeenTour && strategies.length === 0 && goals.length === 0) {
      props.tourActions.setShowTour('TeamLeadTour')
      setHasShownAutoTour(true)
      // Mark tour as seen
      props.userStore.setAccountsUserSetting('hasSeenTeamLeadTour', true)
    }
  }, [props.userStore?.currentAccountsUser, hasShownAutoTour])

  // Start tour when TeamLeadTour is activated
  useEffect(() => {
    if (props.tourActions.showTour() === 'TeamLeadTour') {
      setIsRunning(true)
      setStepIndex(0)
    } else {
      setIsRunning(false)
      // Remove blocking overlay when tour ends
      const style = document.getElementById('tour-blocking-overlay')
      if (style) {
        style.remove()
      }
    }
  }, [props.tourActions.showTour()])

  // Update CSS to only allow clicks on current step's target
  useEffect(() => {
    if (!isRunning) return
    
    // Never apply blocking overlay during tests unless specifically enabled
    if (window.Cypress && !window.cypressAllowTourOverlay) return

    // Remove existing style
    const existingStyle = document.getElementById('tour-blocking-overlay')
    if (existingStyle) {
      existingStyle.remove()
    }

    // Get the target selector for current step
    let currentTarget = ''
    if (stepIndex < steps.length) {
      const step = steps[stepIndex]
      if (step.target !== 'body') {
        currentTarget = step.target
      }
    }

    // Create new style with current target
    const style = document.createElement('style')
    style.id = 'tour-blocking-overlay'
    style.textContent = `
      body * {
        pointer-events: none !important;
      }
      [data-floating-ui-portal] *,
      .react-joyride__tooltip *,
      .react-joyride__spotlight,
      .react-joyride__overlay,
      .react-joyride *,
      .modal *,
      .modal-body input,
      .modal-footer button${currentTarget ? `,\n      ${currentTarget}` : ''} {
        pointer-events: auto !important;
      }
    `
    document.head.appendChild(style)
  }, [isRunning, stepIndex])

  // Helper function to advance to next step
  const advanceStep = () => {
    setStepIndex(prev => prev + 1)
  }

  const steps = [
    // Welcome step
    {
      target: 'body',
      content: (
        <div>
          <h4>Welcome as a Team Lead! 👋</h4>
          <p>
            Congratulations on your team lead role! You're responsible for translating organizational 
            priorities into actionable strategies for your team.
          </p>
          <p>Let's walk through what you need to know and set up.</p>
        </div>
      ),
      disableBeacon: true,
      placement: 'center',
      offset: 5
    },
    
    // Framework primer
    createPrimerStep(getAccountType()),
    
    // Review priorities sequence
    navigationSteps.openMenu(
      <div>
        <h5>Step 1: Review Priorities</h5>
        <p>First, let's review the organizational priorities. Click the menu to navigate.</p>
      </div>
    ),
    navigationSteps.toPriorities(
      <div>
        <h5>View Priorities</h5>
        <p>Click on "Priorities" to see what your organization values most.</p>
      </div>
    ),
    contextSteps.understandingPriorities(
      <div>
        <h4>Understanding Priorities 📌</h4>
        <p>
          These are your organization's guiding principles. As a team lead, you don't create 
          these - they're set by leadership. Your job is to understand them deeply and create 
          strategies that support them.
        </p>
        <p>Take a moment to review each priority and think about how your team can contribute.</p>
      </div>
    ),

    // Strategy creation sequence
    ...sequences.createStrategy(true),

    contextSteps.strategySuccess(
      <div>
        <h4>Great Work! 🎯</h4>
        <p>You've created your first team strategy. Consider adding 1-2 more strategies for different priorities.</p>
        <p>Next, let's set up your personal goals.</p>
      </div>
    ),

    // Goal creation for team leads
    ...sequences.createGoal('team_lead'),

    // Final completion step
    {
      target: 'body',
      content: (
        <div>
          <h4>You're All Set! 🚀</h4>
          <p>As a team lead, you now have:</p>
          <ul>
            <li>✅ Understanding of organizational priorities</li>
            <li>✅ Team strategies that support those priorities</li>
            <li>✅ Personal goals to measure success</li>
          </ul>
          <p><strong>Next Steps:</strong></p>
          <ul>
            <li>📢 Communicate strategies to your team</li>
            <li>👥 Help team members set their individual goals</li>
            <li>📊 Review progress weekly with your team</li>
          </ul>
          <div className="mt-3">
            <button 
              className="btn btn-primary me-2"
              onClick={() => {
                document.querySelector('#home-link')?.click()
                props.tourActions.setShowTour(false)
              }}
            >
              Go to Dashboard 🏠
            </button>
            <button 
              className="btn btn-outline-secondary"
              onClick={() => props.tourActions.setShowTour(false)}
            >
              Explore on My Own
            </button>
          </div>
        </div>
      ),
      disableBeacon: true,
      placement: 'center',
      offset: 5
    }
  ]

  const joyrideStateChanged = (s) => {
    const { action, index, status, type } = s

    if (status === 'finished' || status === 'skipped' || action === ACTIONS.CLOSE) {
      props.tourActions.setShowTour(false)
      setIsRunning(false)
      setStepIndex(0)
      // Clean up CSS blocking overlay
      const style = document.getElementById('tour-blocking-overlay')
      if (style) {
        style.remove()
      }
    } else if (action === ACTIONS.NEXT && type === EVENTS.STEP_AFTER) {
      setStepIndex(index + 1)
    } else if (action === ACTIONS.PREV && type === EVENTS.STEP_AFTER) {
      setStepIndex(index - 1)
    }
  }

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      const style = document.getElementById('tour-blocking-overlay')
      if (style) {
        style.remove()
      }
    }
  }, [])

  // Set up click handlers for target elements to advance tour
  useEffect(() => {
    if (!isRunning) return

    const handleMenuClick = () => {
      if (stepIndex === 2 || stepIndex === 8) {
        setTimeout(() => advanceStep(), 100)
      }
    }

    const handlePrioritiesClick = () => {
      if (stepIndex === 3) {
        setTimeout(() => advanceStep(), 100)
      }
    }

    const handleStrategiesClick = () => {
      if (stepIndex === 9) {
        setTimeout(() => advanceStep(), 100)
      }
    }

    const handleAddButtonClick = () => {
      if (stepIndex === 10 || stepIndex === 17) {
        setTimeout(() => advanceStep(), 1000)
      }
    }

    const handleSaveButtonClick = () => {
      if (stepIndex === 13 || stepIndex === 20) {
        setTimeout(() => advanceStep(), 500)
      }
    }

    const handleGoalsClick = () => {
      if (stepIndex === 16) {
        setTimeout(() => advanceStep(), 100)
      }
    }

    // Add event listeners
    const menuLink = document.querySelector('#menu-link')
    const prioritiesLink = document.querySelector('[data-cy="nav-priorities"]')
    const strategiesLink = document.querySelector('[data-cy="nav-strategies"]')
    const goalsLink = document.querySelector('#goals-link')
    const addButton = document.querySelector('[data-cy="add-button"]')
    const saveButton = document.querySelector('[data-cy="modal-save-button"]')

    if (menuLink) menuLink.addEventListener('click', handleMenuClick)
    if (prioritiesLink) prioritiesLink.addEventListener('click', handlePrioritiesClick)
    if (strategiesLink) strategiesLink.addEventListener('click', handleStrategiesClick)
    if (goalsLink) goalsLink.addEventListener('click', handleGoalsClick)
    if (addButton) addButton.addEventListener('click', handleAddButtonClick)
    if (saveButton) saveButton.addEventListener('click', handleSaveButtonClick)

    // Cleanup
    return () => {
      if (menuLink) menuLink.removeEventListener('click', handleMenuClick)
      if (prioritiesLink) prioritiesLink.removeEventListener('click', handlePrioritiesClick)
      if (strategiesLink) strategiesLink.removeEventListener('click', handleStrategiesClick)
      if (goalsLink) goalsLink.removeEventListener('click', handleGoalsClick)
      if (addButton) addButton.removeEventListener('click', handleAddButtonClick)
      if (saveButton) saveButton.removeEventListener('click', handleSaveButtonClick)
    }
  }, [isRunning, stepIndex])

  return (
    <Joyride
      stepIndex={stepIndex}
      steps={steps} 
      spotlightPadding={5} 
      spotlightClicks={true}
      disableOverlayClose={true}
      disableOverlay={true}
      callback={joyrideStateChanged}
      run={isRunning}
      continuous={true}
      showProgress={true}
      showSkipButton={true}
      styles={{
        options: {
          primaryColor: '#28a745',
          width: 450,
          zIndex: 10002,
        },
        spotlight: {
          backgroundColor: 'rgba(0, 0, 0, 0)',
          border: '2px solid #28a745',
        },
        tooltip: {
          fontSize: '14px',
          padding: '20px',
          zIndex: 10003,
        }
      }}
      locale={{
        back: 'Back',
        close: 'Close',
        last: 'Finish',
        next: 'Next',
        skip: 'Skip Tour'
      }}
    />
  )
}

TeamLeadTour.propTypes = {
  tourActions: PropTypes.object.isRequired,
  userStore: PropTypes.object
}

export default withUserStore(TeamLeadTour)